import { InjectionToken, QueryList } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export interface OptionLike {
  value: any;
  disabled: boolean;
  selected: boolean;
  active: boolean;
  multiple: boolean;
  id: string;
  viewValue: string;
  select(): void;
  deselect(): void;
  setActiveStyles(): void;
  setInactiveStyles(): void;
  getLabel(): string;
  focus(): void;
}

export interface SelectChange<T = any> {
  source: SelectParent;
  value: T;
}

export interface OptionSelectionChange {
  source: OptionLike;
  isUserInput: boolean;
}

/**
 * Contract between `<app-select>` and its projected `<app-option>` children.
 * Decouples the option from its concrete parent so consumers (e.g. the
 * `<app-select-all>` directive) can integrate without depending on
 * `SelectComponent` directly.
 */
export interface SelectParent {
  multiple: boolean;
  disabled: boolean;
  hideSingleSelectionIndicator: boolean;
  compareWith: (a: any, b: any) => boolean;
  readonly options: QueryList<OptionLike>;
  readonly value: any;
  readonly valueChange: Observable<any>;
  readonly stateChanges: Subject<void>;
  /**
   * Called by an `<app-option>` when its selection state changes — either by
   * user interaction (click / Enter / Space) or programmatic call.
   */
  _onOptionSelectionChange(option: OptionLike, isUserInput: boolean): void;
  _onOptionTouched(): void;
  /**
   * Rebuild the selection model and propagate from the options' current
   * `selected` flags. Used by `<app-select-all>` after it toggles options via
   * the programmatic `select()`/`deselect()` path, which the per-option
   * `_onOptionSelectionChange` deliberately ignores (`isUserInput: false`).
   */
  syncSelectionFromOptions(): void;
}

export const SELECT_PARENT = new InjectionToken<SelectParent>('SELECT_PARENT');
